import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { SERVICES } from "@/data/services";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];
const SPANS = [
  "md:col-span-7",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-7",
  "md:col-span-7",
  "md:col-span-5",
  "md:col-span-7",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-7",
  "md:col-span-12",
];

const ServiceModal = ({ service, onClose, onBook }) => (
  <AnimatePresence>
    {service && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="service-modal-overlay"
        onClick={onClose}
        className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-6"
      >
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
          onClick={(e) => e.stopPropagation()}
          data-testid="service-modal"
          className="w-full max-w-lg rounded-t-3xl bg-white p-8 shadow-2xl sm:rounded-3xl"
        >
          <div className="flex items-start justify-between">
            <span className="font-mono text-xs tracking-[0.3em] text-red-700">
              {service.num} — SERVICIO
            </span>
            <button
              data-testid="service-modal-close"
              onClick={onClose}
              aria-label="Cerrar"
              className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-100"
            >
              <X size={16} />
            </button>
          </div>
          <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight text-slate-900">
            {service.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            {service.short}
          </p>
          <button
            data-testid="service-modal-book-btn"
            onClick={() => onBook(service.title)}
            className="mt-8 w-full rounded-full bg-black py-3.5 text-sm font-medium text-white transition-[background-color,transform] duration-300 hover:bg-red-700 active:scale-[0.98]"
          >
            Solicitar este examen
          </button>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const Services = () => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const book = (title) => {
    setActive(null);
    window.dispatchEvent(new CustomEvent("select-service", { detail: title }));
    scrollToId("contacto");
  };

  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="relative mx-auto max-w-7xl px-6 py-24 sm:py-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: EASE }}
        className="mb-16 max-w-2xl"
      >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-red-700">
          01 — Servicios de atención
        </p>
        <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          Atención médica integral: ecografías y laboratorio clínico, todo en
          un solo lugar
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          Tecnología de imagen de última generación y análisis clínicos
          confiables, con un trato cercano en cada visita.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
        {SERVICES.map((s, i) => {
          return (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.12, ease: EASE }}
              data-testid={`service-card-${s.id}`}
              className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-8 transition-[box-shadow,border-color,transform] duration-500 hover:-translate-y-1 hover:border-red-600/30 hover:shadow-xl hover:shadow-red-900/5 ${SPANS[i]}`}
            >
              <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-red-50 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
              <div>
                <div className="flex items-center justify-between">
                  <img
                    data-testid={`service-image-${s.id}`}
                    src={s.image}
                    alt={`Ecografía - ${s.title}`}
                    loading="lazy"
                    className="h-24 w-24 rounded-2xl border border-slate-200 object-cover shadow-sm transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="font-mono text-xs tracking-[0.3em] text-slate-400">
                    {s.num}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-xl font-medium tracking-tight text-slate-900 sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  {s.short}
                </p>
              </div>
              <div className="mt-8 flex items-center gap-3">
                <button
                  data-testid={`service-detail-btn-${s.id}`}
                  onClick={() => setActive(s)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 transition-[border-color,color] duration-300 hover:border-red-600 hover:text-red-700"
                >
                  Ver detalle
                  <ArrowUpRight size={13} />
                </button>
                <button
                  data-testid={`service-book-btn-${s.id}`}
                  onClick={() => book(s.title)}
                  className="rounded-full bg-slate-100 px-4 py-2 text-xs font-medium text-slate-700 transition-[background-color,color] duration-300 hover:bg-black hover:text-white"
                >
                  Agendar
                </button>
              </div>
            </motion.article>
          );
        })}
      </div>

      <ServiceModal
        service={active}
        onClose={() => setActive(null)}
        onBook={book}
      />
    </section>
  );
};

export default Services;
